/**
 * Debt domain (`debt` and `debt_payment` tables).
 *
 * Remaining balance = amount − SUM(debt_payment.amount). A payment can generate
 * a linked transaction ("i_owe" payment = expense; "owed_to_me" collection =
 * income) whose id is stored in `debt_payment.tx_id`. The debt transitions to `settled`
 * automatically when payments cover the amount.
 */
import {
  and,
  asc,
  debt,
  debt_payment,
  desc,
  eq,
  type PowerSyncDrizzleDB,
  sql,
} from "@cashsync/powersync";
import { liveDebtFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { createTransactionMutation } from "./mutations";

export type DebtDirection = "owed_to_me" | "i_owe";

/** Runtime guard: client SQLite types `direction` as string|null. */
export function asDebtDirection(value: string | null | undefined): DebtDirection {
  return value === "i_owe" ? "i_owe" : "owed_to_me";
}

const DATE_KEY_LENGTH = 10;

export interface DebtRow {
  id: string;
  person: string;
  direction: DebtDirection;
  /** Minor units. */
  amount: number;
  currency: string;
  note: string | null;
  due_on: string | null;
  settled: boolean;
}

export interface DebtDraft {
  person: string;
  direction: DebtDirection;
  amount: number;
  currency: string;
  note?: string | null;
  dueOn?: string | null;
}

export interface DebtPaymentDraft {
  debtId: string;
  amount: number;
  paidOn: string;
  /** Optional associated transaction (created in the same operation). */
  txId?: string | null;
  /**
   * Account to materialize payment against as linked transaction.
   * If provided, creates a tx (i_owe → expense, owed_to_me → income) with
   * NULL category and note "Debt: <person>", storing its id in tx_id.
   */
  walletId?: string | null;
}

// ─── Pure logic (testable without DB) ─────────────────────────────────────────

/** Remaining debt balance in minor units (never negative). */
export function debtRemaining(amount: number, totalPaid: number): number {
  return Math.max(amount - totalPaid, 0);
}

/** A debt is settled when payments cover the amount (> 0). */
export function isDebtCovered(amount: number, totalPaid: number): boolean {
  return amount > 0 && totalPaid >= amount;
}

/** Overdue: due_on is prior to today and debt is not settled. */
export function isDebtOverdue(
  dueOn: string | null | undefined,
  settled: boolean,
  todayKey: string,
): boolean {
  if (!dueOn || settled) {
    return false;
  }
  return dueOn.slice(0, DATE_KEY_LENGTH) < todayKey;
}

/** Transaction kind generated by payment according to debt direction. */
export function debtPaymentTxKind(direction: DebtDirection): "expense" | "income" {
  return direction === "i_owe" ? "expense" : "income";
}

/** Canonical note for txs linked to debt payments. */
export function debtPaymentTxNote(person: string): string {
  return `Debt: ${person}`;
}

/** `paid_on` is date-text (YYYY-MM-DD); linked tx requires full ISO. */
export function debtPaymentOccurredAtISO(paidOn: string): string {
  if (paidOn.includes("T")) {
    return paidOn;
  }
  return new Date(`${paidOn}T12:00:00`).toISOString();
}

// ─── Queries ──────────────────────────────────────────────────────────────────

/** Live debts of the user. */
export function getDebtsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(debt)
    .where(and(eq(debt.user_id, userId), ...liveDebtFilters()))
    .orderBy(asc(debt.settled), asc(debt.due_on), desc(debt.created_at));
}

/** Live debts with aggregated payment SUM (to derive remaining balance). */
export function getDebtsWithPaidQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: debt.id,
      person: debt.person,
      direction: debt.direction,
      amount: debt.amount,
      currency: debt.currency,
      note: debt.note,
      due_on: debt.due_on,
      settled: debt.settled,
      created_at: debt.created_at,
      paid: sql<number>`COALESCE(SUM(${debt_payment.amount}), 0)`.as("paid"),
    })
    .from(debt)
    .leftJoin(debt_payment, eq(debt_payment.debt_id, debt.id))
    .where(and(eq(debt.user_id, userId), ...liveDebtFilters()))
    .groupBy(debt.id)
    .orderBy(asc(debt.settled), asc(debt.due_on), desc(debt.created_at));
}

export type DebtWithPaid = Awaited<
  ReturnType<ReturnType<typeof getDebtsWithPaidQuery>["execute"]>
>[number];

/** Payments for a debt, from newest to oldest. */
export function getDebtPaymentsQuery(db: PowerSyncDrizzleDB, debtId: string) {
  return db
    .select()
    .from(debt_payment)
    .where(eq(debt_payment.debt_id, debtId))
    .orderBy(desc(debt_payment.paid_on));
}

export type DebtPaymentRow = Awaited<
  ReturnType<ReturnType<typeof getDebtPaymentsQuery>["execute"]>
>[number];

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createDebtMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: DebtDraft,
): Promise<string> {
  if (draft.amount <= 0) {
    throw new Error("The debt amount must be positive");
  }
  const id = generateId();
  const now = nowISO();
  await db.insert(debt).values({
    id,
    user_id: userId,
    person: draft.person.trim(),
    direction: draft.direction,
    amount: draft.amount,
    currency: draft.currency,
    note: draft.note ?? null,
    due_on: draft.dueOn ?? null,
    settled: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateDebtMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    person: string;
    direction: DebtDirection;
    amount: number;
    currency: string;
    note: string | null;
    due_on: string | null;
    settled: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.person !== undefined) {
    finalUpdates.person = finalUpdates.person.trim();
  }
  await patchRowVerified(db, debt, debt.id, id, finalUpdates, options);
}

/** Mark settled / reopen manually. */
export async function setDebtSettledMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  settled: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await updateDebtMutation(db, id, { settled: settled ? 1 : 0 }, options);
}

/** Logical deletion: payments (and their txs) remain in history. */
export async function softDeleteDebtMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, debt, debt.id, id, { deleted_at: nowISO() }, options);
}

export interface AddDebtPaymentResult {
  paymentId: string;
  /** id of linked transaction, if requested to create. */
  txId: string | null;
  /** true if this payment settled the debt (automatic settlement). */
  settled: boolean;
}

/**
 * Adds a partial payment. If `draft.walletId` is provided, first creates
 * linked tx (expense/income by direction, NULL category, note "Debt: …")
 * and saves its id in `debt_payment.tx_id`. After insert, recalculates payment
 * sum and automatically marks `settled = 1` if it covers the amount.
 */
export async function addDebtPaymentMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: DebtPaymentDraft,
): Promise<AddDebtPaymentResult> {
  if (draft.amount <= 0) {
    throw new Error("The payment amount must be positive");
  }
  const rows = await db.select().from(debt).where(eq(debt.id, draft.debtId)).limit(1);
  const [debtRow] = rows;
  if (!debtRow) {
    throw new Error("Deuda no encontrada");
  }

  let txId = draft.txId ?? null;
  if (!txId && draft.walletId) {
    txId = await createTransactionMutation(db, userId, {
      kind: debtPaymentTxKind(asDebtDirection(debtRow.direction)),
      amount: draft.amount,
      currency: debtRow.currency ?? "USD",
      walletId: draft.walletId,
      categoryId: null,
      occurredAtISO: debtPaymentOccurredAtISO(draft.paidOn),
      note: debtPaymentTxNote(debtRow.person ?? ""),
    });
  }

  const paymentId = generateId();
  const now = nowISO();
  await db.insert(debt_payment).values({
    id: paymentId,
    user_id: userId,
    debt_id: draft.debtId,
    tx_id: txId,
    amount: draft.amount,
    paid_on: draft.paidOn,
    created_at: now,
    updated_at: now,
  });

  // Automatic settlement: payment sum covers debt amount.
  const totals = await db
    .select({ paid: sql<number>`COALESCE(SUM(${debt_payment.amount}), 0)`.as("paid") })
    .from(debt_payment)
    .where(eq(debt_payment.debt_id, draft.debtId));
  const paid = totals[0]?.paid ?? 0;
  const covered = isDebtCovered(debtRow.amount ?? 0, paid);
  if (covered && debtRow.settled !== 1) {
    await setDebtSettledMutation(db, draft.debtId, true);
  }

  return { paymentId, txId, settled: covered };
}
